import moment from "moment-timezone"
import { categoryLabel, clubLabel, daysLabel, instantLabel, organizerLabel, plural, shortDateLabel } from "./plannerLabels"
import { proposalMessage } from "./proposalMessages"
import { fieldLabel } from "./proposalHistory"

// The words of sending and answers (Phase C3; wayfinder ticket 16): what To send, Waiting for answer, the review and the editor's Answer
// tab say. CP strings are English; the text copied for the organizer is in the list's language.

// The days of a proposal from its instants, in the country's calendar.
export const instantDays = ({ startDate, endDate }, timeZone) => ({ start: moment(startDate).tz(timeZone).format("YYYY-MM-DD"),
  end: moment(endDate).tz(timeZone).format("YYYY-MM-DD") })
export const instantDaysLabel = (dates, timeZone) => daysLabel(instantDays(dates, timeZone))
// A club as the queues give it: the proposal's stored club (its city under `location`) or the list's (`city`).
export const queueClubLabel = club => clubLabel(club && { ...club, city: club.city ?? club.location?.city })
// An organizer of a queue, by its company name (Phase C3's API): a person organizer has none, and the names may be unavailable.
export const queueOrganizerName = (organizer, unavailable = []) => (!organizer?.companyName && unavailable.includes("organizerNames")
  ? "Organizer (name unavailable)" : organizerLabel(organizer))

// Why a draft cannot be sent to its organizer (the sending's checks, Phase C2a; ticket 18).
const REASONS = {
  organizerMissing: "No organizer",
  otherOrganizer: "It belongs to another organizer",
  organizerNotActive: "The organizer's account is not activated",
  organizerWithoutSportyaOS: "No SportyaOS access: create the organizer's account first",
  clubMissing: "No club chosen (choose a club, TBA or No club)",
  clubNotActive: "The club is not activated",
  organizerNotAtClub: "The organizer is not one of the club's activated organizers",
  playModeMissing: "No play mode",
}
export const reasonLabel = reason => REASONS[reason] || reason
export const reasonsLabel = reasons => (reasons || []).map(reasonLabel).join(" · ")

export const LANGUAGES = [["ro", "Română"], ["bg", "Български"], ["en", "English"]]
export const languageLabel = language => (LANGUAGES.find(([key]) => key === language) || [])[1] || language
export const HOW = { email: "Email from Sportya", outsidePlatform: "I send it myself" }

// How a list or a reminder reached the organizer (Phase C2b deliveries): an email accepted by the mail server is "delivered".
const DELIVERY = { sent: "delivered", sending: "on its way", failed: "not delivered", unconfirmed: "may not have left" }
export const deliveryLabel = delivery => (delivery ? DELIVERY[delivery.status] || delivery.status : null)
// "Last list sent 9 Oct by email to office@aceline.ro · delivered", or sent outside the platform.
export const lastSendingLine = (sending, timeZone) => {
  if (!sending) return "No list sent yet"
  const day = shortDateLabel(sending.at, timeZone)
  if (sending.how !== "email") return `Last list sent ${day} outside the platform`
  const to = (sending.delivery?.recipients || []).map(({ email }) => email).filter((email, index, all) => all.indexOf(email) === index)
  return `Last list sent ${day} by email${to.length ? ` to ${to.join(", ")}` : ""}${sending.delivery ? ` · ${deliveryLabel(sending.delivery)}` : ""}`
}
// "Email failed" (ticket 16): which addresses did not get the list, and why.
export const failedLine = (sending, timeZone) => {
  const missed = (sending?.delivery?.recipients || []).filter(({ status }) => ["failed", "unconfirmed"].includes(status))
  const who = missed.map(({ email, status }) => `${email} (${status === "failed" ? "refused" : "may not have left"})`).join(", ")
  return `The list sent ${sending ? instantLabel(sending.at, timeZone) : ""} did not reach the organizer${who ? `: ${who}` : ""}. Its proposals are Proposed anyway. `
    + "Send it again, or record that you sent it yourself."
}

// The sections of the organizer's list (ticket 16), as the review shows them.
export const LIST_SECTIONS = [
  ["new", "New", "Ticked ones become Proposed when you send."],
  ["changed", "Changed since the last list", "The list shows what changed."],
  ["waiting", "Still waiting for an answer", "Sent before, no change."],
  ["confirmed", "Confirmed, not published yet", "For information."],
  ["withdrawn", "Withdrawn since the last list", "Shown once, then left out."],
]
// What changed since the list the organizer has, in the review: "Changed: dates (sent Sat 7 – Sun 8 Nov)".
const CHANGE_FIELDS = { startDate: "dates", endDate: "dates", club: "club", organizer: "organizer", category: "category", playMode: "play mode",
  sportType: "sport", programType: "circuit", name: "name", competitions: "competitions" }
// A change told to the organizer may name a field that is no list column (ticket 09: a court surface, a competition's level): it is named
// as the proposal's history names it.
const changedFields = changes => [...new Set((changes || []).map(({ field }) => {
  const [name] = field.split(".")
  return CHANGE_FIELDS[name] || fieldLabel(name).toLowerCase()
}))]
export const changedFieldsLabel = changes => changedFields(changes).join(", ")
export const changesLine = (changes, sent, timeZone) => {
  const fields = changedFields(changes)
  if (!fields.length) return null
  const dates = sent && fields.includes("dates") ? ` (sent ${instantDaysLabel(sent, timeZone)})` : ""
  return `Changed: ${fields.join(", ")}${dates}`
}

// The refusals of the sending, the resending, the reminder and the answers (Phases C2a, C2b), worded for where they show; a refusal
// that names proposals names them.
const named = details => (details?.proposals || []).map(({ number, reasons }) => (reasons?.length ? `${number} (${reasonsLabel(reasons)})` : number))
  .filter(Boolean).join(", ")
const SENDING = {
  PROPOSAL_LIST_CHANGED: "The organizer's list changed meanwhile. It is shown again: review it, then send.",
  PROPOSAL_NOT_IN_LIST: "A proposal is no longer in the organizer's list. The list is shown again: review it, then send.",
  PROPOSAL_NOT_SENDABLE: "Some proposals can't be sent.",
  PROPOSAL_REVISION_CONFLICT: "A proposal of the list changed meanwhile. The list is shown again: review it, then send.",
  PROPOSAL_NOT_FOUND: "A proposal of the list no longer exists. The list is shown again.",
  PROPOSAL_EMAIL_NOT_CONFIGURED: "Emails can't be sent from this server: the calendar team's reply address is not set. Send the list yourself.",
  INVALID_PROPOSAL_SENDING: "The addresses were refused. Check them, tick “I checked…” again, then send.",
  INVALID_PROPOSAL_REMINDER: "The addresses were refused. Check them, tick “I checked…” again, then send.",
  PROPOSAL_DELIVERY_SENDING: "An email to this address may still leave. Wait a minute, then look at the delivery again.",
  PROPOSAL_SENDING_NOT_CURRENT: "A newer list was sent to this organizer meanwhile: it is the one to send again.",
  PROPOSAL_SENDING_NOT_FOUND: "This list no longer exists. The queues are read again.",
  PROPOSAL_NOT_ANSWERABLE: "Already answered, withdrawn or published meanwhile.",
  INVALID_PROPOSAL_ANSWERS: "The answers were refused: check the names (at most 200 characters) and the note (at most 1,000).",
}
export const sendingMessage = (code, details) => {
  const text = SENDING[code] || proposalMessage(code)
  const which = named(details)
  return which ? `${text} ${which}.` : text
}
// The refusal of a batch of answers, in its toast: nothing was saved, and the proposals are read again (the rows that did not change
// keep their choices).
const ANSWERS = {
  PROPOSAL_REVISION_CONFLICT: "A proposal changed meanwhile, so no answer was saved. The proposals are shown again: answer the changed ones again.",
  PROPOSAL_NOT_ANSWERABLE: "A proposal was answered, withdrawn or published meanwhile, so no answer was saved. The proposals are shown again.",
  PROPOSAL_NOT_SENDABLE: "A draft can't be confirmed directly: it could not be sent to its organizer. No answer was saved.",
}
export const answerMessage = (code, details) => (ANSWERS[code] ? [ANSWERS[code], named(details)].filter(Boolean).join(" ") + (named(details) ? "." : "")
  : sendingMessage(code, details))

// "2 confirmed, 1 declined".
export const answerTally = answers => {
  const confirmed = answers.filter(({ decision }) => decision === "confirmed").length
  const declined = answers.length - confirmed
  return [confirmed && `${confirmed} confirmed`, declined && `${declined} declined`].filter(Boolean).join(", ")
}
export const answersCount = answers => plural(answers.length, "answer")

// "Copy as text" (ticket 16; integrator default 2026-10-21): the organizer's list for WhatsApp or the phone, one line per proposal —
// number · category · dates · club · competitions — under the organizer's name and each section, in the list's language (the words of
// the email, common/tournamentProposalList).
const COPY = {
  ro: { dow: ["dum", "lun", "mar", "mie", "joi", "vin", "sâm"], mon: ["ian", "feb", "mar", "apr", "mai", "iun", "iul", "aug", "sep", "oct", "nov", "dec"],
    clubs: { tba: "Club de anunțat", none: "Fără club" }, notSet: "probe de stabilit",
    sections: { new: "Noi", changed: "Modificate față de lista trimisă", waiting: "Așteaptă răspunsul dumneavoastră",
      confirmed: "Confirmate de dumneavoastră, încă nepublicate", withdrawn: "Retrase de Sportya" } },
  bg: { dow: ["нд", "пн", "вт", "ср", "чт", "пт", "сб"], mon: ["яну", "фев", "мар", "апр", "май", "юни", "юли", "авг", "сеп", "окт", "ное", "дек"],
    clubs: { tba: "Клубът предстои да бъде обявен", none: "Без клуб" }, notSet: "дисциплините предстоят",
    sections: { new: "Нови", changed: "Променени след изпратения списък", waiting: "Очакват вашия отговор",
      confirmed: "Потвърдени от вас, все още непубликувани", withdrawn: "Оттеглени от Sportya" } },
  en: { dow: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"], mon: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    clubs: { tba: "Club to be announced", none: "No club" }, notSet: "competitions to be set",
    sections: { new: "New", changed: "Changed since the list we sent", waiting: "Waiting for your answer",
      confirmed: "Confirmed by you, not published yet", withdrawn: "Withdrawn by Sportya" } },
}
const partsOf = day => { const date = new Date(`${day}T00:00:00Z`); return { dow: date.getUTCDay(), date: date.getUTCDate(), mon: date.getUTCMonth(), year: date.getUTCFullYear() } }
const copyDays = (words, { start, end }) => {
  const a = partsOf(start), b = partsOf(end)
  const right = `${words.dow[b.dow]} ${b.date} ${words.mon[b.mon]} ${b.year}`
  if (start === end) return right
  return `${words.dow[a.dow]} ${a.date}${a.mon === b.mon && a.year === b.year ? "" : ` ${words.mon[a.mon]}`}${a.year === b.year ? "" : ` ${a.year}`} – ${right}`
}
const copyClub = (words, club) => (!club ? "" : club.kind !== "club" ? words.clubs[club.kind] || "" : [club.clubName, club.city ?? club.location?.city].filter(Boolean).join(", "))
const copyCompetitions = (words, competitions = []) => (competitions.length
  ? competitions.map(({ level, gameType }) => `${gameType === "doubles" ? "D" : "S"}${Number.isInteger(level) ? `${level}.0` : level ?? "—"}`).join(" · ")
  : words.notSet)
export const listAsText = ({ name, sections, language, settings, timeZone }) => {
  const words = COPY[language] || COPY.en
  const lines = [name]
  LIST_SECTIONS.forEach(([key]) => {
    if (!sections[key]?.length) return
    lines.push("", words.sections[key])
    sections[key].forEach(item => {
      const content = item.content || item.sent
      lines.push([item.number, categoryLabel(settings, content.category), copyDays(words, instantDays(content, timeZone)), copyClub(words, content.club),
        copyCompetitions(words, content.competitions)].filter(Boolean).join(" · "))
    })
  })
  return lines.join("\n")
}
